import { useLocalization } from "@fluent/react"
import { Alert, AlertTitle, Box, Collapse, Link, Stack, Typography } from "@mui/material"
import { useState } from "react"
import { TemplateIssue, TemplateValidationReport } from "./reportTemplatesApi"

interface TemplateReportViewProps {
    report: TemplateValidationReport
    /** Show a success line when the template has no errors and no warnings. */
    showSuccess?: boolean
}

const IssueItem = ({ issue }: { issue: TemplateIssue }) => {
    const { l10n } = useLocalization()

    // Codes whose text depends only on the tag are translated; for the rest the backend's
    // (English) message is shown as is, since it names details the report has no fields for.
    const key = `report-template-issue-${issue.code}`
    const translated = !!l10n.getBundle(key)
    const message = translated ? l10n.getString(key) : issue.message

    return (
        <Box component="li" sx={{ mb: 0.75 }}>
            <Typography variant="body2">
                {translated && issue.tag && (
                    <Box component="code" sx={{ fontFamily: "monospace", fontWeight: 600, mr: 0.75 }}>
                        {issue.tag}
                    </Box>
                )}
                {message}
            </Typography>
            {translated && issue.suggestion && (
                <Typography variant="caption" display="block">
                    {l10n.getString("report-template-issue-suggestion", { suggestion: issue.suggestion }, `Did you mean ${issue.suggestion}?`)}
                </Typography>
            )}
            {(issue.location || issue.context) && (
                <Typography variant="caption" color="text.secondary" display="block">
                    {[issue.location, issue.context ? `„${issue.context}”` : null].filter(Boolean).join(" – ")}
                </Typography>
            )}
        </Box>
    )
}

const IssueList = ({ issues }: { issues: TemplateIssue[] }) => (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {issues.map((issue, index) => <IssueItem key={`${issue.code}-${index}`} issue={issue} />)}
    </Box>
)

export const TemplateReportView = ({ report, showSuccess = true }: TemplateReportViewProps) => {
    const { l10n } = useLocalization()
    const [fixesOpen, setFixesOpen] = useState(false)

    const { errors, warnings, fixes } = report

    return (
        <Stack spacing={1}>
            {errors.length > 0 && (
                <Alert severity="error">
                    <AlertTitle>
                        {l10n.getString("report-template-report-errors", { count: errors.length }, `Errors (${errors.length}) – the template cannot be saved`)}
                    </AlertTitle>
                    <IssueList issues={errors} />
                </Alert>
            )}

            {errors.length === 0 && warnings.length === 0 && showSuccess && (
                <Alert severity="success">
                    {l10n.getString("report-template-report-valid", null, "The template is valid.")}
                </Alert>
            )}

            {warnings.length > 0 && (
                <Alert severity="warning">
                    <AlertTitle>
                        {l10n.getString("report-template-report-warnings", { count: warnings.length }, `Warnings (${warnings.length})`)}
                    </AlertTitle>
                    <IssueList issues={warnings} />
                </Alert>
            )}

            {fixes.length > 0 && (
                <Alert severity="info">
                    <AlertTitle sx={{ mb: 0 }}>
                        {l10n.getString("report-template-report-fixes", { count: fixes.length }, `Automatic corrections (${fixes.length})`)}
                        {" "}
                        <Link component="button" variant="body2" onClick={() => setFixesOpen(open => !open)}>
                            {fixesOpen
                                ? l10n.getString("report-template-report-hide", null, "hide")
                                : l10n.getString("report-template-report-show", null, "show")}
                        </Link>
                    </AlertTitle>
                    <Collapse in={fixesOpen}>
                        <Box sx={{ mt: 1 }}>
                            <IssueList issues={fixes} />
                        </Box>
                    </Collapse>
                </Alert>
            )}
        </Stack>
    )
}
